import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, HostBinding, Inject, Input, OnInit, Output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { first } from 'rxjs';
import { BannerComponent } from 'src/app/shared/banner.component';
import { SidePanelConfig, initSidePanelConfig } from 'src/app/shared/components/side-panel/side-panel.model';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';

export const DEFAULT_SIDE_PANEL_WIDTH = '500px';

@Component({
    selector: 'nova-side-panel',
    templateUrl: './side-panel.component.html',
    styleUrls: ['./side-panel.component.scss'],
    standalone: true,
    imports: [MatIconModule, BannerComponent]
})
export class SidePanelComponent implements OnInit {
    @Input() config: SidePanelConfig = initSidePanelConfig();

    @Output() formClosed = new EventEmitter<boolean>();
    @Output() cancelClicked = new EventEmitter<boolean>();

    /**
     * Signal that tracks whether the side panel is currently showing.
     * Using a signal ensures OnPush change detection works correctly.
     */
    isShowing = signal(false);

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly _confirmDialogService: ConfirmDialogService
    ) {}

    ngOnInit(): void {
        if (this.config.isOpenByDefault) {
            this.openPanel();
        }
    }

    openPanel() {
        this.isShowing.set(true);
    }

    closePanel() {
        this.isShowing.set(false);
        const sidebar = this.document.getElementById('side-panel');
        sidebar?.scrollTo(0, 0);
        this.formClosed.emit(true);
    }

    cancel() {
        if (this.config.overrideCancelEnabled) {
            this.cancelClicked.emit(true);
            return;
        }

        if (!this.config.isCancelDialogEnabled || !this.config.cancelConfirmationDialogConfig) {
            this.closePanel();
            return;
        }

        this.confirmPanelClose();
    }

    confirmPanelClose() {
        this._confirmDialogService
            .showDialog(this.config.cancelConfirmationDialogConfig)
            .pipe(first())
            .subscribe((result) => {
                if (result === this.config.cancelConfirmationDialogCloseValue) {
                    this.closePanel();
                }
            });
    }

    onOverlayClick() {
        if (!this.config.closeOnOverlayClick) {
            return;
        }

        this.closePanel();
    }

    @HostBinding('class.show-side-panel') get isShowingSidePanel(): boolean {
        return this.isShowing();
    }

    @HostBinding('style.--panel-width') get panelWidth(): string {
        return this.config.width ?? DEFAULT_SIDE_PANEL_WIDTH;
    }

    @HostBinding('style.--min-panel-width') get minPanelWidth(): string {
        return this.config.minWidth ?? DEFAULT_SIDE_PANEL_WIDTH;
    }
}
